import type { Role } from '@/lib/permissions';
import { canRead } from '@/lib/permissions';

/**
 * Dashboard privacy for screenshares / walk-ups.
 *
 * Users who can see financials on `/dashboard` (today: super_admin) land
 * with the snapshot hidden by default so the daily brief, cash, runway, and
 * recent activity aren't on screen the moment Atlas loads. They toggle Show
 * when ready.
 *
 * Preference is local to this browser (`atlas:dashboard:defaultVisible`):
 *   unset / `'0'` → hide on load (product default)
 *   `'1'`         → show on load
 */

export const DASHBOARD_DEFAULT_VISIBLE_KEY = 'atlas:dashboard:defaultVisible';

/** Privacy mode only matters when the dashboard actually has sensitive content. */
export function dashboardPrivacyApplies(role: Role | null): boolean {
  return canRead(role, 'financials');
}

/** Default visibility on a fresh load / navigation to `/dashboard`. */
export function readDashboardDefaultVisible(role: Role | null): boolean {
  if (!dashboardPrivacyApplies(role)) return true;
  try {
    return window.localStorage.getItem(DASHBOARD_DEFAULT_VISIBLE_KEY) === '1';
  } catch {
    return false;
  }
}

export function writeDashboardDefaultVisible(visible: boolean): void {
  try {
    window.localStorage.setItem(DASHBOARD_DEFAULT_VISIBLE_KEY, visible ? '1' : '0');
  } catch {
    /* quota / private mode — caller keeps in-memory state */
  }
}
